import type { PostHog } from 'posthog-js';
import type { BrandContext } from '@/lib/brand/types';

export const PRODUCT_ANALYTICS_EVENTS = {
  AUTH_CLEVER_SIGN_IN_CLICKED: 'auth_clever_sign_in_clicked',
  AUTH_CREDENTIAL_SIGN_IN_SUBMITTED: 'auth_credential_sign_in_submitted',
  AUTH_CREDENTIAL_SIGN_IN_SUCCEEDED: 'auth_credential_sign_in_succeeded',
  AUTH_CREDENTIAL_SIGN_IN_FAILED: 'auth_credential_sign_in_failed',
  AUTH_SIGNUP_SUBMITTED: 'auth_signup_submitted',
  AUTH_SIGNUP_SUCCEEDED: 'auth_signup_succeeded',
  AUTH_SIGNUP_FAILED: 'auth_signup_failed',
  ASSIGNMENT_TYPE_SELECTED: 'assignment_type_selected',
  ASSIGNMENT_CREATED: 'assignment_created',
  ASSIGNMENT_CREATE_FAILED: 'assignment_create_failed',
  ASSIGNMENT_EDIT_OPENED: 'assignment_edit_opened',
  ASSIGNMENT_UPDATED: 'assignment_updated',
  ASSIGNMENT_UPDATE_FAILED: 'assignment_update_failed',
  ASSIGNMENT_DELETED: 'assignment_deleted',
  ASSIGNMENT_DELETE_CLICKED: 'assignment_delete_clicked',
  ASSIGNMENT_CARD_ACTION_CLICKED: 'assignment_card_action_clicked',
  STUDENT_ASSIGNMENT_LAUNCH_REQUESTED: 'student_assignment_launch_requested',
  STUDENT_ASSIGNMENT_OPENED: 'student_assignment_opened',
  STUDENT_ASSIGNMENT_LAUNCH_FAILED: 'student_assignment_launch_failed',
  CLASSROOM_OPENED: 'classroom_opened',
  CLASSROOM_CREATED: 'classroom_created',
  CLASSROOM_CREATE_FAILED: 'classroom_create_failed',
  CLASSROOM_ROSTER_IMPORTED: 'classroom_roster_imported',
  CLASSROOM_ROSTER_UPDATED: 'classroom_roster_updated',
  GROUP_SET_CREATED: 'group_set_created',
  GROUP_SET_UPDATED: 'group_set_updated',
  GROUP_SET_DELETED: 'group_set_deleted',
  REPORT_OPENED: 'report_opened',
  REPORT_DETAIL_EXPANDED: 'report_detail_expanded',
  REPORT_COMMENT_CREATED: 'report_comment_created',
  REPORT_REACTION_TOGGLED: 'report_reaction_toggled',
  REPORT_LIKE_TOGGLED: 'report_like_toggled',
  REPORT_PRINT_MENU_OPENED: 'report_print_menu_opened',
  REPORT_PRINT_REQUESTED: 'report_print_requested',
  REPORT_PRINT_SECTION_TOGGLED: 'report_print_section_toggled',
  REPORT_PRINT_OPTION_TOGGLED: 'report_print_option_toggled',
  ANALYTICS_PAGE_LOADED: 'analytics_page_loaded',
  ANALYTICS_FILTER_CHANGED: 'analytics_filter_changed',
  ANALYTICS_DATA_LOADED: 'analytics_data_loaded',
  ANALYTICS_DATA_FAILED: 'analytics_data_failed',
  NOTEBOOK_CREATED: 'notebook_created',
  NOTEBOOK_OPENED: 'notebook_opened',
  NOTEBOOK_UPDATED: 'notebook_updated',
  NOTEBOOK_TEMPLATE_SELECTED: 'notebook_template_selected',
  NOTEBOOK_TEMPLATE_CREATED: 'notebook_template_created',
  NOTEBOOK_TEMPLATE_UPDATED: 'notebook_template_updated',
  RESOURCE_OPENED: 'resource_opened',
  RESOURCE_LINK_CREATED: 'resource_link_created',
  RESOURCE_LINK_UPDATED: 'resource_link_updated',
  RESOURCE_LINK_DELETED: 'resource_link_deleted',
  TASK_ACTION_CLICKED: 'task_action_clicked',
} as const;

export type ProductAnalyticsEventName =
  (typeof PRODUCT_ANALYTICS_EVENTS)[keyof typeof PRODUCT_ANALYTICS_EVENTS];

type AnalyticsPrimitive = string | number | boolean | null;
export type ProductAnalyticsProperties = Record<string, unknown>;
export type ProductAnalyticsContext = Pick<
  BrandContext,
  'brandKey' | 'partnerKey'
>;

const MAX_STRING_LENGTH = 240;
const MAX_ARRAY_LENGTH = 20;

const FORBIDDEN_PROPERTY_KEYS = new Set([
  'accesscode',
  'assignmenturl',
  'authorization',
  'authtoken',
  'bearertoken',
  'callbackurl',
  'classroomdisplayname',
  'description',
  'destination',
  'displayname',
  'email',
  'firstname',
  'href',
  'label',
  'lastname',
  'name',
  'password',
  'productkey',
  'rawurl',
  'responsebody',
  'responseexcerpt',
  'roomkey',
  'roomcode',
  'stacktrace',
  'studentname',
  'teachername',
  'text',
  'ticket',
  'token',
  'url',
]);

const SAFE_POSTHOG_SYSTEM_PROPERTY_KEYS = new Set([
  '$active_session',
  '$browser',
  '$browser_language',
  '$browser_version',
  '$device_id',
  '$device_type',
  '$insert_id',
  '$lib',
  '$lib_version',
  '$os',
  '$os_name',
  '$os_version',
  '$screen_height',
  '$screen_width',
  '$session_id',
  '$set',
  '$set_once',
  '$time',
  '$viewport_height',
  '$viewport_width',
  '$window_id',
]);

const POSTHOG_URL_SYSTEM_PROPERTY_KEYS = new Set([
  '$current_url',
  '$host',
  '$initial_current_url',
  '$initial_referrer',
  '$pathname',
  '$prev_pageview_pathname',
  '$referrer',
  '$session_entry_url',
]);

const ID_SEGMENT_PATTERN = /^c[a-z0-9]{12,}$/i;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const ROUTE_SEGMENT_LABELS: Record<string, string> = {
  assignment: ':assignmentId',
  assignments: ':assignmentId',
  classroom: ':classroomId',
  classrooms: ':classroomId',
  'create-space': ':assignmentSetId',
  'challenge-set': ':assignmentSuperSetId',
  groupsets: ':groupSetId',
  'group-sets': ':groupSetId',
  individual: ':assignmentId',
  notebooks: ':notebookId',
  organizations: ':organizationId',
  sessions: ':sessionId',
  students: ':studentId',
  users: ':userId',
};

const normalizeKey = (key: string) =>
  key
    .replace(/[$\s-]/g, '')
    .replace(/_/g, '')
    .toLowerCase();

const isForbiddenKey = (key: string) => {
  if (key.startsWith('$')) return false;
  return FORBIDDEN_PROPERTY_KEYS.has(normalizeKey(key));
};

const isPostHogUrlSystemPropertyKey = (key: string) => {
  const normalized = key.toLowerCase();
  return (
    POSTHOG_URL_SYSTEM_PROPERTY_KEYS.has(key) ||
    normalized.includes('url') ||
    normalized.includes('href') ||
    normalized.includes('pathname') ||
    normalized.includes('referrer')
  );
};

const truncateString = (value: string) =>
  value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...`
    : value;

const stripQueryAndHash = (value: string) => value.split(/[?#]/)[0] || '/';

const extractPath = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://learnwithexplore.com';
    return stripQueryAndHash(new URL(trimmed, base).pathname);
  } catch {
    return stripQueryAndHash(trimmed);
  }
};

export const normalizeAnalyticsPath = (
  value?: string | null
): string | undefined => {
  if (!value) return undefined;
  const path = extractPath(value);
  if (!path) return undefined;

  const segments = path.split('/').filter(Boolean);
  const normalized = segments.map((segment, index) => {
    if (ID_SEGMENT_PATTERN.test(segment) || UUID_PATTERN.test(segment)) {
      const parent = segments[index - 1];
      return parent ? ROUTE_SEGMENT_LABELS[parent] || ':id' : ':id';
    }
    return segment;
  });

  return normalized.length ? `/${normalized.join('/')}` : '/';
};

export const getCurrentRouteTemplate = () => {
  if (typeof window === 'undefined') return undefined;
  return normalizeAnalyticsPath(
    `${window.location.pathname}${window.location.search}`
  );
};

const isPrimitive = (value: unknown): value is AnalyticsPrimitive =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

export const getAnalyticsContextProperties = (
  context?: ProductAnalyticsContext | null
): ProductAnalyticsProperties => {
  if (!context) return {};

  return {
    brand_key: context.brandKey,
    ...(context.partnerKey ? { partner_key: context.partnerKey } : {}),
  };
};

export const withAnalyticsContext = (
  properties: ProductAnalyticsProperties | null | undefined,
  context?: ProductAnalyticsContext | null
): ProductAnalyticsProperties => ({
  ...(properties ?? {}),
  ...getAnalyticsContextProperties(context),
});

const sanitizeValue = (
  value: unknown
): AnalyticsPrimitive | string[] | null => {
  if (typeof value === 'string') {
    return truncateString(value);
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'boolean' || value === null) {
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .filter((entry): entry is AnalyticsPrimitive => isPrimitive(entry))
      .slice(0, MAX_ARRAY_LENGTH)
      .map((entry) =>
        typeof entry === 'string' ? truncateString(entry) : String(entry)
      );
  }
  return null;
};

export const sanitizeAnalyticsProperties = (
  properties: ProductAnalyticsProperties = {}
) => {
  const sanitized: Record<string, AnalyticsPrimitive | string[]> = {};

  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === 'undefined' || isForbiddenKey(key)) continue;
    const normalizedKey = key === 'pathname' ? 'route_template' : key;
    const sanitizedValue = sanitizeValue(
      normalizedKey === 'route_template' && typeof value === 'string'
        ? normalizeAnalyticsPath(value)
        : value
    );
    if (sanitizedValue !== null) {
      sanitized[normalizedKey] = sanitizedValue;
    }
  }

  if (!sanitized.route_template) {
    const routeTemplate = getCurrentRouteTemplate();
    if (routeTemplate) sanitized.route_template = routeTemplate;
  }

  return sanitized;
};

export const captureProductEvent = (
  posthog: Pick<PostHog, 'capture'> | null | undefined,
  eventName: ProductAnalyticsEventName,
  properties?: ProductAnalyticsProperties
) => {
  posthog?.capture(eventName, sanitizeAnalyticsProperties(properties));
};

export const sanitizePostHogPayload = <T extends { properties?: unknown }>(
  payload: T | null
): T | null => {
  if (!payload) {
    return payload;
  }
  if (!payload.properties || typeof payload.properties !== 'object') {
    return payload;
  }

  const properties = payload.properties as ProductAnalyticsProperties;
  const sanitized: Record<string, unknown> = {};

  const setRouteTemplate = (key: string, value: unknown) => {
    if (key === '$host') return;
    if (typeof value !== 'string') return;
    const routeTemplate = normalizeAnalyticsPath(value);
    if (!routeTemplate) return;

    const normalizedKey = key.toLowerCase();
    const targetKey =
      normalizedKey.includes('referrer') ||
      normalizedKey.includes('prev_pageview')
        ? 'referrer_route_template'
        : normalizedKey.includes('initial') || normalizedKey.includes('entry')
          ? 'entry_route_template'
          : 'route_template';
    if (!sanitized[targetKey]) {
      sanitized[targetKey] = routeTemplate;
    }
  };

  for (const [key, value] of Object.entries(properties)) {
    if (key.startsWith('$')) {
      if (isPostHogUrlSystemPropertyKey(key)) {
        setRouteTemplate(key, value);
        continue;
      }
      if (!SAFE_POSTHOG_SYSTEM_PROPERTY_KEYS.has(key)) {
        continue;
      }
      if (key === '$set' || key === '$set_once') {
        sanitized[key] = value;
        continue;
      }
      const sanitizedSystemValue = sanitizeValue(value);
      if (sanitizedSystemValue !== null) {
        sanitized[key] = sanitizedSystemValue;
      }
      continue;
    }
    if (typeof value === 'undefined' || isForbiddenKey(key)) continue;
    const normalizedKey = key === 'pathname' ? 'route_template' : key;
    const sanitizedValue = sanitizeValue(
      normalizedKey === 'route_template' && typeof value === 'string'
        ? normalizeAnalyticsPath(value)
        : value
    );
    if (sanitizedValue !== null) {
      sanitized[normalizedKey] = sanitizedValue;
    }
  }

  return {
    ...payload,
    properties: sanitized,
  };
};

export const normalizeSessionEventName = (eventName: string) => {
  const match = eventName.match(/^(GET|POST|PUT|PATCH|DELETE) (.+)$/);
  if (!match) return eventName;
  const normalizedPath = normalizeAnalyticsPath(match[2]);
  return normalizedPath ? `${match[1]} ${normalizedPath}` : eventName;
};

export const buildPostHogIdentityKey = (
  distinctId: string,
  traits: ProductAnalyticsProperties
) => JSON.stringify({ distinctId, traits });
